import React, { Fragment, memo, type ComponentType, type ReactNode } from 'react';
import { styled } from '@compiled/react';
import type { IssueId } from '@atlassian/jira-shared-types';
import type { PercentPosition } from '@atlassian/jira-software-deployments-common-table/src/components/table/common/utils/timeline';
import type { TimelineMode } from '@atlassian/jira-software-deployments-common-table/src/model/timeline';
import type { Deployment } from '../../../../common/types';
import {
	type GroupedDeployments,
	areDeploymentsEqual,
	getGroupedDeployments,
} from '../../../../common/utils/deployments';
import DeploymentsDialog from './deployments-dialog';
import EnvironmentsLabel from './environments-label';

type Props = {
	issueId: IssueId;
	deployments: Deployment[];
	showMultipleDeploymentsIcon: boolean;
	getPositionFromDate: (date: Date) => PercentPosition;
	timelineMode: TimelineMode;
};

/**
 * Responsible for drawing all the circles for all deployments for an issue.
 */
const Circles = ({
	deployments: deploymentsSorted,
	issueId,
	showMultipleDeploymentsIcon,
	getPositionFromDate,
	timelineMode,
}: Props) => {
	const deploymentGroups = getGroupedDeployments(
		deploymentsSorted,
		getPositionFromDate,
		timelineMode,
	);

	return deploymentGroups.map((group: GroupedDeployments, groupIndex: number) => {
		const groupId = group.deployments[0].id;
		const isFirstGroup = groupIndex === 0;

		return (
			<Fragment key={groupId}>
				<CircleWrapper
					data-testid="software-deployments.ui.timeline.deployments-bar.circles.deployment-icon-container"
					leftPosition={group.left}
					rightPosition={group.right}
				>
					<DeploymentsDialog
						issueId={issueId}
						deployments={group.deployments}
						showMultipleDeploymentsIcon={showMultipleDeploymentsIcon}
						groupIndex={groupIndex}
					/>
				</CircleWrapper>
				{isFirstGroup && (
					<EnvironmentsLabelWrapper leftPosition={100 - group.right}>
						<EnvironmentsLabel sortedDeployments={group.deployments} />
					</EnvironmentsLabelWrapper>
				)}
			</Fragment>
		);
	});
};

const isEqual = (
	{
		deployments: prevDeps,
		showMultipleDeploymentsIcon: prevShowMultipleDeploymentsIcon,
		timelineMode: prevTimelineMode,
	}: Props,
	{
		deployments: nextDeps,
		showMultipleDeploymentsIcon: nextShowMultipleDeploymentsIcon,
		timelineMode: nextTimelineMode,
	}: Props,
): boolean =>
	prevTimelineMode === nextTimelineMode &&
	prevShowMultipleDeploymentsIcon === nextShowMultipleDeploymentsIcon &&
	areDeploymentsEqual(prevDeps, nextDeps);

type CircleWrapperProps = {
	leftPosition: number;
	rightPosition: number;
	children: ReactNode;
	'data-testid': string;
	title?: string;
	onClick?: () => void;
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CircleWrapper: ComponentType<CircleWrapperProps> = styled.div<CircleWrapperProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	left: ({ leftPosition }) => `${leftPosition}%`,
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	right: ({ rightPosition }) => `${rightPosition}%`,
	position: 'absolute',
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'center',
});
CircleWrapper.displayName = 'CircleWrapper';

type EnvironmentsLabelWrapperProps = {
	leftPosition: number;
	children: ReactNode;
};
// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const EnvironmentsLabelWrapper = styled.div<EnvironmentsLabelWrapperProps>({
	// eslint-disable-next-line @atlaskit/ui-styling-standard/no-dynamic-styles -- Ignored via go/DSP-18766
	left: ({ leftPosition }) => `${leftPosition}%`,
	position: 'absolute',
});

export default memo<Props>(Circles, isEqual);
